import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Tune } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { IRootReducer } from "../../../reducers";
import { updateUserPreferencesRequest } from "../../../actions/user-info";
import AgencyFilter from "../../integrated-dispatch-feed/dispatch-event-feed-grid/agency-filters/agency-filter";
import { useDispatch } from "react-redux";
import { GetAgencyJogIds, JogMap, JogsForDispatchFiltering } from "../../../reducers/states/agency/jogs";
import { GetAgencyFilterLabel } from "../../../reducers/states/agency/wsp";
import { useLocation } from "react-router-dom";
import { JogNames, JogsNamesTooltip } from "../../common/associated-jogs";

const JogSelectionModal: FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const userPrefStore = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [showAgencies, setShowAgencies] = useState(false);
  const [selectedJogs, setSelectedJogs] = useState([] as string[]);
  const [selectedAgencies, setSelectedAgencies] = useState([] as string[]);
  const jogsBeforeEdit = useRef([] as string[]);
  const agenciesBeforeEdit = useRef([] as string[]);

  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  const MaxJogDisplayCount = isMd ? 3 : 1;
  const isAboveMax = userPrefStore.jogFilters?.length > MaxJogDisplayCount;
  const JogTerm = "JOG" + (userPrefStore.jogFilters?.length > 1 ? "s" : "");
  const useAgencyColor = false;

  useEffect(() => {
    switch (location.pathname) {
      case "/dashboard":
      case "/feed":
        setShowAgencies(true);
        break;
      default:
        setShowAgencies(false);
    }
  }, [location.pathname]);

  const isSelectionUnchanged = (newSelections: string[], pastSelections: string[]): boolean => {
    const newSlectionCopy = [...newSelections]; // Create copy, to avoid changing order by reference.
    newSlectionCopy.sort();
    pastSelections.sort();
    return newSlectionCopy.length === pastSelections.length
      && newSlectionCopy.every((value, index) => value === pastSelections[index]);
  }

  useEffect(() => {
    if (!selectedJogs.length) {
      // Require selection of at least one JOG
      setIsSaveDisabled(true);
    } else {
      const isJogSelectionChanged = !isSelectionUnchanged(selectedJogs, jogsBeforeEdit.current);
      setIsSaveDisabled(isJogSelectionChanged ? false : isSelectionUnchanged(selectedAgencies, agenciesBeforeEdit.current));
    }

  }, [selectedAgencies, selectedJogs])

  const handleEdit = () => {
    setSelectedJogs([...userPrefStore.jogFilters]);
    setSelectedAgencies([...userPrefStore.agencyFilters]);
    jogsBeforeEdit.current = [...userPrefStore.jogFilters];
    agenciesBeforeEdit.current = [...userPrefStore.agencyFilters].sort();
    setIsSaveDisabled(true);
    setIsOpen(true);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleSaveEdits = () => {
    setIsOpen(false);

    dispatch(
      updateUserPreferencesRequest({
        ...userPrefStore,
        agencyFilters: selectedAgencies.sort(),
        jogFilters: selectedJogs,
      }),
    );
  };

  const handleAgencySelected = (event: ChangeEvent<HTMLInputElement>): void => {
    const agency = event.target.value;

    let updatedAgencies = [...selectedAgencies];

    if (event.target.checked) {
      // Add agency
      updatedAgencies.push(agency);
    } else {
      // Remove agency
      let index = updatedAgencies.indexOf(agency);
      if (index > -1) updatedAgencies.splice(index, 1);
    }
    updatedAgencies.sort();

    setSelectedAgencies(updatedAgencies);
  };

  // Added so use of isDisjointFrom will compile, instead of changing TS target to ES2024.
  interface Set<T> {
    isDisjointFrom(other: Set<T>): boolean;
  }

  const handleJogSelected = (event: ChangeEvent<HTMLInputElement>): void => {
    const jogId = event.target.value;
    const jog = JogMap.get(jogId);

    const updatedJogs = new Set([...selectedJogs]);
    const updatedAgencies = new Set([...selectedAgencies]);

    if (event.target.checked) {
      // Add JOG and all of its agencies
      updatedJogs.add(jogId);
      jog?.agencies.forEach((agency) => {
        if (agency.isFilter) {
          updatedAgencies.add(agency.id);
        }
      });
    } else {
      // Remove JOG
      updatedJogs.delete(jogId);

      // Remove all of the JOG's agencies,
      jog?.agencies.forEach((agency) => {
        // ...if the agency is selected,
        // - (checking isFilter avoids running a loop for the has() call for non-filter agencies)
        if(agency.isFilter && updatedAgencies.has(agency.id)) {          
          let jogIds = GetAgencyJogIds(agency.id);

          // ...unless it's also in a different JOG that's currently selected
          if((jogIds?.size || 0) < 2 || (jogIds as unknown as Set<string>).isDisjointFrom(updatedJogs as unknown as Set<string>)) {
            updatedAgencies.delete(agency.id);
          }
        }
      });
    }

    setSelectedJogs([...updatedJogs.values()]);
    setSelectedAgencies([...updatedAgencies.values()]);
  };

  const CountOfJogs = (): JSX.Element => {
    return (
      <Tooltip title={<JogsNamesTooltip label={"Selected JOG"} jogIds={userPrefStore.jogFilters} />}>
        <span>
          {userPrefStore.jogFilters?.length} {JogTerm}
        </span>
      </Tooltip>
    );
  };

  return (
    <>
      <Grid container wrap="nowrap" alignItems={"center"}>
        <Grid item>
          {!isMd && isAboveMax ? <CountOfJogs /> : <JogNames tooltipLabel="Selected JOG" maxJogDisplayCount={3} delimiter=" | " jogIds={userPrefStore.jogFilters} />}
        </Grid>
        <Grid item>
          <Tooltip title="Edit selected JOGs">
            <IconButton color="inherit" onClick={handleEdit}>
              <Tune />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <Dialog
        open={isOpen}
        onClose={handleCancel}
        fullScreen={isFullScreen}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: isFullScreen ? "100%" : "90vh",
            maxHeight: isFullScreen ? "100%" : "800px",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "2em" }}>
          JOG or Group
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <Box>
            <Grid container alignItems="center" justifyContent={"center"}>
              {JogsForDispatchFiltering.map((jog) => {
                return (
                  <Grid item key={jog.id}>
                    <AgencyFilter
                      props={{
                        label: jog.shortName,
                        selectedFilters: selectedJogs,
                        agencyId: jog.id,
                        isFeedDown: false,
                        tooltip: jog.name,
                        useAgencyColor: useAgencyColor,
                        handleAgencySelect: handleJogSelected,
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>

            {showAgencies && (
              <>
                <Divider style={{ margin: "8px 0" }} />
                <Typography component="h2" variant="h5">
                  Agencies
                </Typography>
              </>
            )}
          </Box>

          {showAgencies && (
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                p: 2,
              }}
            >
              {JogsForDispatchFiltering.map((jog) => {
                return selectedJogs.includes(jog.id) ? (
                  <Box key={jog.id}>
                    <Divider style={{ margin: "8px" }} />
                    <Tooltip title={jog.name} followCursor>
                      <Typography component="h3" variant="h6" pl={1}>
                        {jog.name}
                      </Typography>
                    </Tooltip>
                    <Grid container alignItems="center" pl={1} pr={0.5}>
                      {jog.agencies.map((agency, index) => {

                        if (!agency.isFilter) return undefined;

                        return (
                          <Grid item key={index}>
                            <AgencyFilter
                              props={{
                                label: agency.shortName,
                                selectedFilters: selectedAgencies,
                                agencyId: agency.id,
                                isFeedDown: false,
                                tooltip: `${agency.name} (${GetAgencyFilterLabel(agency.id)})`,
                                useAgencyColor: useAgencyColor,
                                handleAgencySelect: handleAgencySelected,
                              }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ) : undefined;
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit" variant="contained">
            Cancel
          </Button>
          <Tooltip title={isSaveDisabled ? (selectedJogs.length ? "Selections have not changed" : "Select at least one JOG or Group") : ""}>
            <Box ml={1}>
              <Button onClick={handleSaveEdits} color="primary" variant="contained" disabled={isSaveDisabled}>
                Save
              </Button>
            </Box>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JogSelectionModal;
